import { useMemo } from 'react';

import { TFunction } from 'i18next';
import { useGetAuxValueGroups } from 'queries';
import { useTranslation } from 'react-i18next';

import { isAuxValueUnused } from '../utils/utils';
import mapToOptions from 'utils/mapToOptions';

import { AuxTranslation, AuxValueGroup } from 'RestAPIs/AuxiliaryValues';

import { GridColDef } from '@mui/x-data-grid-pro';

import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';
import ColorCell from 'components/shared/Table/TableCell/DataGrid/ColorCell';

const useColumnDefinitions = () => {
  const { t } = useTranslation('common');
  const { data } = useGetAuxValueGroups();

  const auxValueGroups = data?.map((item) => {
    return { ...item, name: t(`auxGroups.${item?.code}`) };
  });

  return useMemo(
    () => columnDefinitions(t, auxValueGroups),
    [t, auxValueGroups]
  );
};

const columnDefinitions = (t: TFunction, auxValueGroups?: AuxValueGroup[]) =>
  [
    {
      field: 'colorCode',
      minWidth: 50,
      headerName: t('auxValues.color'),
      renderCell: ColorCell,
      align: 'center'
    },
    {
      field: 'code',
      headerName: t('auxValues.code'),
      hideable: false,
      minWidth: 100
    },
    {
      field: 'auxValueGroupId',
      headerName: t('auxValues.group'),
      minWidth: 110,
      flex: 1,
      valueGetter: (value: string | number) => {
        if (!auxValueGroups) return value;        

        const selectedAuxValueGroup = auxValueGroups.find(
          (group) => group.auxValueGroupId === value
        );
        return selectedAuxValueGroup?.code
          ? t(`auxGroups.${selectedAuxValueGroup?.code}`)
          : '';
      },
      valueOptions: mapToOptions(auxValueGroups ?? [], 'name', 'name'),
      type: 'singleSelect'
    },
    {
      field: 'translations',
      flex: 1,
      minWidth: 110,
      headerName: t('auxValues.description'),
      valueGetter: (value: AuxTranslation) => value?.[0]?.name || ''
    },
    {
      field: 'sortOrder',
      minWidth: 80,
      headerName: t('auxValues.sortOrder')
    },
    {
      field: 'flags',
      headerName: t('auxValues.notInUse'),
      minWidth: 80,
      type: 'boolean',
      valueGetter: (value: string[]) => isAuxValueUnused(value) || value?.[0],
      renderCell: BooleanCell
    }
  ] as GridColDef[];

export default useColumnDefinitions;
